import { Card, Row, Col, Statistic, Typography, Tooltip, Button, Select, Spin } from 'antd';
import './reporting-page.css';
import { Header } from '../header/header';
import { LineChart } from '@mantine/charts';
import RankingCard from './ranking-card';
import { reportingService } from '../../services/reporting-service';
import { useEffect, useState } from 'react';
import { CaretDownOutlined, CaretUpOutlined, ExclamationOutlined, InfoCircleOutlined, MinusOutlined, SwapOutlined, SyncOutlined } from '@ant-design/icons';
import { Metrics, RatingDto, RatingsResponse } from '../../dto/reporting-models';
import dayjs from 'dayjs';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../utils/auth-utils';
import { scopes } from '../../authConfig';
import { strings } from '../../lang';

const { Title } = Typography;

interface PagePros {
  hideHeader?: boolean | undefined;
}

const ReportingPage = ({ hideHeader: showHeader }: PagePros) => {
  const { instance, inProgress } = useMsal();
  const [isSpinning, setIsSpinning] = useState(false);
  const [userMetrics, setUserMetrics] = useState<Metrics | null>();
  const [userMetricsMonthly, setUserMetricsMonthly] = useState<Metrics | null>();
  const [ratingData, setRatingData] = useState<RatingsResponse | null>();
  const [isDailyView, setIsDailyView] = useState<boolean>(true);

  const [showRate, setShowRate] = useState<number | null>(null);
  const [bookings, setBookings] = useState<number | null>(null);
  const [reachedRate, setReachedRate] = useState<number | null>(null);
  const [bookingsRate, setBookingsRate] = useState<number | null>(null);
  const [sameNextDayBooking, setSameNextDayBooking] = useState<number | null>(null);
  const [assignmentsPerNettAppointments, setAssignmentsPerNettAppointments] = useState<number | null>(null);
  const [nettAppointments, setNettAppointments] = useState<number | null>(null);

  const [bookingsDiff, setBookingsDiff] = useState<number | null>(null);
  const [reachedRateDiff, setReachedRateDiff] = useState<number | null>(null);
  const [nearestAppDiff, setNearestAppDiff] = useState<number | null>(null);
  const [bookingsRateDiff, setBookingsRateDiff] = useState<number | null>(null);
  const [nettoTerminenDiff, setNettoTerminenDiff] = useState<number | null>(null);

  const [reachedRateDiffMonthly, setReachedRateDiffMonthly] = useState<number | null>(null);
  const [showRateDiffMonthly, setShowRateDiffMonthly] = useState<number | null>(null);
  const [nearestAppDiffMonthly, setNearestAppDiffMonthly] = useState<number | null>(null);
  const [bookingsRateDiffMonthly, setBookingsRateDiffMonthly] = useState<number | null>(null);
  const [nettoTerminenDiffMonthly, setNettoTerminenDiffMonthly] = useState<number | null>(null);
  const [assignmentsPerNettAppointmentsDiff, setAssignmentsPerNettAppointmentsDiff] = useState<number | null>(null);

  const [showRateClarification, setShowRateClarification] = useState<string | null>(null);
  const [showRateNextTime, setShowRateNextTime] = useState<string | null>(null);

  useEffect(() => {
    if (isDailyView) {
      fetchData(isDailyView);
      fetchRatingData('dep');
    } else {
      fetchData(isDailyView);
    }
  }, [isDailyView]);

  useEffect(() => {
    if (userMetrics || userMetricsMonthly) {
      const metrics = userMetrics ?? userMetricsMonthly;
      const showRateMetric = metrics?.metricData?.todayMetrics.filter((metric) => metric.type === 'ShowRate')[0];
      const bookingsMetric = metrics?.metricData?.todayMetrics.filter((metric) => metric.type === 'Bookings')[0];
      const reachedRateMetric = metrics?.metricData?.todayMetrics.filter((metric) => metric.type === 'ReachedRate')[0];
      const bookingsRateMetric = metrics?.metricData?.todayMetrics.filter((metric) => metric.type === 'BookingRate')[0];
      const today = metrics?.metricData?.todayMetrics.filter((metric) => metric.type === 'NearestAppointmentsPerToday')[0];
      const assignmentsPerNettAppointmentsMetric = metrics?.metricData?.todayMetrics.filter((metric) => metric.type === 'AssignmentsPerNettAppointment')[0];
      const nettAppointments = metrics?.metricData?.todayMetrics.filter((metric) => metric.type === 'NettAppointments')[0];

      setSameNextDayBooking(today ? +today.value : null);
      setShowRate(showRateMetric ? +showRateMetric.value : null);
      setBookings(bookingsMetric ? +bookingsMetric.value : null);
      setReachedRate(reachedRateMetric ? +reachedRateMetric.value : null);
      setBookingsRate(bookingsRateMetric ? +bookingsRateMetric.value : null);
      setNettAppointments(nettAppointments ? +nettAppointments.value : null);

      if (userMetrics) {
        setBookingsDiff(calculateBookingsDiff(+bookingsMetric!.value));
        setReachedRateDiff(calculateReachedRate(metrics!));
        setNearestAppDiff(calculateNearestAppPercentage(metrics!));
        setBookingsRateDiff(calculateBookingsRateDiff(metrics!));
        setNettoTerminenDiff(calculateNettoTerminen(metrics!));
        setShowRateClarification(getShowRateClarification());
        setShowRateNextTime(getNextUpdateTimeForShowRate());
      } else {
        setAssignmentsPerNettAppointments(assignmentsPerNettAppointmentsMetric ? +assignmentsPerNettAppointmentsMetric.value : null);
        setAssignmentsPerNettAppointmentsDiff(calculateAssignmentsPerNettAppointmentDiff(metrics!));
        setReachedRateDiffMonthly(calculateReachedRate(metrics!));
        setNearestAppDiffMonthly(calculateNearestAppPercentage(metrics!));
        setBookingsRateDiffMonthly(calculateBookingsRateDiff(metrics!));
        setNettoTerminenDiffMonthly(calculateNettoTerminen(metrics!));

        setShowRateDiffMonthly(calculateShowRateDiff(metrics!));
      }
    }
  }, [userMetrics, userMetricsMonthly]);

  const fetchData = async (daily: boolean) => {
    // get access token
    setIsSpinning(true);
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    if (daily) {
      const metrics = await reportingService.getSelfMetricsAsync(token.accessToken);
      setUserMetricsMonthly(null);
      if (metrics?.metricData) setUserMetrics(metrics);
    } else {
      const metrics = await reportingService.getSelfMonthlyMetricsAsync(token.accessToken);
      setUserMetrics(null);
      if (metrics?.metricData) setUserMetricsMonthly(metrics);
    }

    setIsSpinning(false);
  };

  const fetchRatingData = async (selection: string) => {
    setIsSpinning(true);

    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const rating = await reportingService.getRatingAsync(token.accessToken, selection === 'team');
    setRatingData(rating);

    setIsSpinning(false);
  };

  const calculateBookingsDiff = (bookingsToday: number) => {
    const bookingsYesterday = userMetrics?.metricData.pastMetrics.filter((metric) => metric.type === 'Bookings')[0].value;
    return bookingsToday - +bookingsYesterday!;
  };

  const calculateReachedRate = (metrics: Metrics) => {
    const now = metrics?.metricData.todayMetrics.filter((metric) => metric.type === 'ReachedRate')[0].value;
    const reachedRateYesterday = metrics?.metricData.pastMetrics.filter((metric) => metric.type === 'ReachedRate')[0].value;
    if (+now === 0) {
      return -+reachedRateYesterday!;
    }
    const dif = +now - +reachedRateYesterday!;
    return dif;
  };

  const calculateNearestAppPercentage = (metrics: Metrics) => {
    const now = metrics?.metricData?.todayMetrics.filter((metric) => metric.type === 'NearestAppointmentsPerToday')[0].value;
    const yesterday = metrics?.metricData?.pastMetrics.filter((metric) => metric.type === 'NearestAppointmentsPerToday')[0].value;
    if (+now === 0) {
      return -yesterday!;
    }
    return +now - +yesterday!;
  };

  const calculateBookingsRateDiff = (metrics: Metrics) => {
    const now = metrics?.metricData.todayMetrics.filter((metric) => metric.type === 'BookingRate')[0].value;
    const pastBookingRate = metrics?.metricData.pastMetrics.filter((metric) => metric.type === 'BookingRate')[0].value;
    return +now - +pastBookingRate!;
  };

  const getNextUpdateTimeForShowRate = () => {
    const nextTimeUtc = userMetrics?.metricData.todayMetrics.filter((metric) => metric.type === 'NextAppointmentUpdateTime')[0].value;
    return nextTimeUtc !== '' ? `${strings.reportingNextUpdateAt} ${dayjs.utc(nextTimeUtc).local().format('HH:mm')}` : strings.reportingNoAppointmentsToday;
  };

  const calculateShowRateDiff = (metrics: Metrics) => {
    var pastShowRate = metrics?.metricData.pastMetrics.filter((metric) => metric.type === 'ShowRate')[0];
    if (pastShowRate) {
      return +metrics?.metricData.todayMetrics.filter((metric) => metric.type === 'ShowRate')[0].value - +pastShowRate.value!;
    }
    return 0;
  };

  const getShowRateClarification = () => {
    const consideredAppointments = userMetrics?.metricData.todayMetrics.filter((metric) => metric.type === 'ConsideredAppointmentAmount')[0].value;
    const appointmentsToGo = userMetrics?.metricData.todayMetrics.filter((metric) => metric.type === 'AppointmentsToGoAmount')[0].value;

    return `${strings.reportingAppointmentsConsidered}: ${consideredAppointments}
    ${strings.reportingAppointmentsToGo}: ${appointmentsToGo}`;
  };

  const convertDataToPercentageRating = (ratingDto: RatingDto | null) => {
    return ratingDto
      ? {
          currentUserPosition: ratingDto?.currentUserPosition,
          currentUserCriteriaCount: !Number.isInteger(ratingDto?.currentUserCriteriaCount)
            ? parseFloat((ratingDto?.currentUserCriteriaCount! * 100).toFixed(1))
            : ratingDto?.currentUserCriteriaCount! * 100,
          leaderboard: ratingDto?.leaderboard
            .sort((a, b) => b.count - a.count)
            .map((booker) => ({
              ...booker,
              count: Number.isInteger(booker.count) ? booker.count * 100 : parseFloat((booker.count * 100).toFixed(1)),
            })),
        }
      : null;
  };

  const calculateNettoTerminen = (metric: Metrics) => {
    const today = metric?.metricData.todayMetrics.filter((metric) => metric.type === 'NettAppointments')[0].value;
    const yesterday = metric?.metricData.pastMetrics.filter((metric) => metric.type === 'NettAppointments')[0].value;
    return +today! - +yesterday!;
  };

  const calculateAssignmentsPerNettAppointmentDiff = (metrics: Metrics) => {
    const now = metrics?.metricData.todayMetrics.filter((metric) => metric.type === 'AssignmentsPerNettAppointment')[0];
    const past = metrics?.metricData.pastMetrics.filter((metric) => metric.type === 'AssignmentsPerNettAppointment')[0];
    if (now) {
      return +now.value - +past.value!;
    }
    return 0;
  };

  const getDiffDirection = (diff: number | null) => {
    if (diff && diff > 0) {
      return <CaretUpOutlined style={{ color: 'green' }} />;
    } else if (diff && diff < 0) {
      return <CaretDownOutlined style={{ color: 'red' }} />;
    } else {
      return <MinusOutlined style={{ color: 'var(--text-secondary)' }} />;
    }
  };

  return (
    <>
      {!showHeader && <Header />}
      <div className="activity-page">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h3 className="activity-title">{strings.reportingActivity}</h3>
          <Button onClick={() => fetchData(isDailyView)} type="link">
            <SyncOutlined style={{ fontSize: '20px', color: 'var(--text-secondary)' }} spin={isSpinning} />
          </Button>
        </div>
        <Card className="activity-card" style={{ paddingLeft: '5px', paddingRight: '30px' }}>
          {userMetrics?.metricData?.todayMetrics?.filter((action) => action.type === 'WentOffline') &&
            userMetrics?.metricData?.todayMetrics?.filter((action) => action.type === 'WentOffline').length >= 3 && (
              <section className="warning-section">
                <div className="warning-message">
                  <ExclamationOutlined style={{ color: 'red', paddingLeft: '5px', paddingRight: '10px' }} />
                  {strings.reportingWarningMessage}
                </div>
              </section>
            )}
          <Row gutter={16}>
            <Button style={{ marginBottom: '10px', marginLeft: '20px' }} onClick={() => setIsDailyView(!isDailyView)}>
              <SwapOutlined />
              {isDailyView ? strings.reporting.periodSwitch.monthly : strings.reporting.periodSwitch.daily}
            </Button>
            <div style={{ paddingLeft: '53px', display: 'flex', justifyContent: 'center', width: '100%' }}>
              {isDailyView ? (
                <Col span={5}>
                  <Statistic title={<b>{strings.reportingBookings}</b>} value={bookings ?? 0} valueStyle={{ fontWeight: '600', fontSize: '48px', lineHeight: '1', marginBottom: '10px' }} />
                  <span>
                    {getDiffDirection(bookingsDiff)}
                    {bookingsDiff ?? 0} {strings.reportingVsYesterday}{' '}
                    <Tooltip title={strings.reportingComparedToSameTimeYesterday}>
                      {' '}
                      <InfoCircleOutlined style={{ color: 'var(--text-secondary)', marginLeft: '2px' }} />
                    </Tooltip>
                  </span>
                </Col>
              ) : (
                <Col span={5}>
                  <Statistic
                    title={<b>Assignments per Net Appointment</b>}
                    value={assignmentsPerNettAppointments?.toFixed(1) ?? '0'}
                    valueStyle={{ fontWeight: '600', fontSize: '48px', lineHeight: '1', marginBottom: '10px' }}
                  />
                  <span>
                    {getDiffDirection(assignmentsPerNettAppointmentsDiff)}
                    {assignmentsPerNettAppointmentsDiff?.toFixed(1)} {strings.reportingVsLastMonth}{' '}
                    <Tooltip title={strings.reportingComparedToSameTimeLastMonth}>
                      {' '}
                      <InfoCircleOutlined style={{ color: 'var(--text-secondary)', marginLeft: '2px' }} />
                    </Tooltip>
                  </span>
                </Col>
              )}
              <Col span={4}>
                <Statistic
                  title={<b>{strings.reportingLeadTime}</b>}
                  value={sameNextDayBooking ? (sameNextDayBooking * 100).toFixed(1) : 0}
                  suffix={sameNextDayBooking === 0 ? '' : '%'}
                  valueStyle={{ fontWeight: '600', fontSize: '48px', lineHeight: '1', marginBottom: '10px' }}
                />
                <span>
                  {getDiffDirection(isDailyView ? nearestAppDiff : nearestAppDiffMonthly)}
                  {isDailyView ? (nearestAppDiff ? (nearestAppDiff * 100).toFixed(1) : 0) : nearestAppDiffMonthly ? (nearestAppDiffMonthly * 100).toFixed(1) : 0}%{' '}
                  {isDailyView ? strings.reportingVsYesterday : strings.reportingVsLastMonth}{' '}
                  <Tooltip title={isDailyView ? strings.reportingComparedToSameTimeYesterday : strings.reportingComparedToSameTimeLastMonth}>
                    {' '}
                    <InfoCircleOutlined style={{ color: 'var(--text-secondary)', marginLeft: '2px' }} />
                  </Tooltip>
                </span>
              </Col>
              <Col span={4}>
                <Statistic
                  title={<b>{strings.reportingReachedRate}</b>}
                  value={reachedRate ? (reachedRate * 100).toFixed(1) : 0}
                  suffix={reachedRate === 0 ? '' : '%'}
                  valueStyle={{ fontWeight: '600', fontSize: '48px', lineHeight: '1', marginBottom: '10px' }}
                />

                <span>
                  {getDiffDirection(isDailyView ? reachedRateDiff : reachedRateDiffMonthly)}
                  {isDailyView ? (reachedRateDiff ? (reachedRateDiff * 100).toFixed(1) : 0) : reachedRateDiffMonthly ? (reachedRateDiffMonthly * 100).toFixed(1) : 0}%{' '}
                  {isDailyView ? strings.reportingVsYesterday : strings.reportingVsLastMonth}{' '}
                  <Tooltip title={isDailyView ? strings.reportingComparedToSameTimeYesterday : strings.reportingComparedToSameTimeLastMonth}>
                    {' '}
                    <InfoCircleOutlined style={{ color: 'var(--text-secondary)', marginLeft: '2px' }} />
                  </Tooltip>
                </span>
              </Col>
              <Col span={4}>
                <Statistic
                  title={<b>{strings.reportingBookingRate}</b>}
                  value={bookingsRate ? (bookingsRate * 100).toFixed(1) : 0}
                  suffix={bookingsRate === 0 ? '' : '%'}
                  valueStyle={{ fontWeight: '600', fontSize: '48px', lineHeight: '1', marginBottom: '10px' }}
                />
                <span>
                  {getDiffDirection(isDailyView ? bookingsRateDiff : bookingsRateDiffMonthly)}
                  {isDailyView ? ((bookingsRateDiff ? bookingsRateDiff : 0) * 100).toFixed(1) : ((bookingsRateDiffMonthly ? bookingsRateDiffMonthly : 0) * 100).toFixed(1)}%{' '}
                  {isDailyView ? strings.reportingVsYesterday : strings.reportingVsLastMonth}{' '}
                  <Tooltip title={isDailyView ? strings.reportingComparedToSameTimeYesterday : strings.reportingComparedToSameTimeLastMonth}>
                    {' '}
                    <InfoCircleOutlined style={{ color: 'var(--text-secondary)', marginLeft: '2px' }} />
                  </Tooltip>
                </span>
              </Col>
              <Col span={4}>
                <Statistic
                  title={<b>{strings.reportingShowRate}</b>}
                  value={showRate ? (showRate * 100).toFixed(1) : '-'}
                  suffix={showRate === 0 ? '' : '%'}
                  valueStyle={{ fontWeight: '600', fontSize: '48px', lineHeight: '1', marginBottom: '10px' }}
                />
                {isDailyView ? (
                  <span>
                    {showRateNextTime}
                    <Tooltip title={showRateClarification}>
                      {' '}
                      <InfoCircleOutlined style={{ color: 'var(--text-secondary)', marginLeft: '2px' }} />
                    </Tooltip>
                  </span>
                ) : (
                  <span>
                    {getDiffDirection(showRateDiffMonthly)}
                    {((showRateDiffMonthly || 0) * 100).toFixed(1)} {strings.reportingVsLastMonth}{' '}
                    <Tooltip title={strings.reportingComparedToSameTimeLastMonth}>
                      {' '}
                      <InfoCircleOutlined style={{ color: 'var(--text-secondary)', marginLeft: '2px' }} />
                    </Tooltip>
                  </span>
                )}
              </Col>
              <Col span={4}>
                <Statistic title={<b>{strings.reportingNettermine}</b>} value={nettAppointments || 0} valueStyle={{ fontWeight: '600', fontSize: '48px', lineHeight: '1', marginBottom: '10px' }} />
                <span>
                  {getDiffDirection(isDailyView ? nettoTerminenDiff : nettoTerminenDiffMonthly)}
                  {isDailyView ? nettoTerminenDiff : nettoTerminenDiffMonthly} {isDailyView ? strings.reportingVsYesterday : strings.reportingVsLastMonth}{' '}
                  <Tooltip title={isDailyView ? strings.reportingComparedToSameTimeYesterday : strings.reportingComparedToSameTimeLastMonth}>
                    {' '}
                    <InfoCircleOutlined style={{ color: 'var(--text-secondary)', marginLeft: '2px' }} />
                  </Tooltip>
                </span>
              </Col>
            </div>
            <Col span={24}>
              <Title level={5} style={{ marginBottom: '20px', marginLeft: '20px' }}>
                {isDailyView ? strings.reportingCallPerHour : strings.reportingCallPerDay}
              </Title>
              <LineChart
                h={200}
                data={
                  isDailyView
                    ? userMetrics?.hourlyData?.map((el) => ({
                        hour: +el.hour,
                        amount: el.amount,
                        pastAmount: el.pastAmount,
                      })) || []
                    : userMetricsMonthly?.hourlyData?.map((el) => ({
                        hour: +el.hour,
                        amount: el.amount,
                        pastAmount: el.pastAmount,
                      })) || []
                }
                dataKey="hour"
                series={[
                  { name: 'amount', color: 'blue.6', label: isDailyView ? strings.reportingCallPerHour : strings.reportingCallPerDay },
                  { name: 'pastAmount', color: 'gray.6', label: isDailyView ? strings.reportingCallPerHourPast : strings.reportingCallPerDayPast },
                ]}
                curveType="linear"
              />
            </Col>
          </Row>
        </Card>
        <Select
          options={[
            { value: 'dep', label: strings.reporting.lblDepView },
            { value: 'team', label: strings.reporting.lblTeamView },
          ]}
          defaultValue={strings.reporting.lblDepView}
          style={{ marginBottom: '10px', width: '200px' }}
          onChange={(value) => fetchRatingData(value)}
        />
        <Row gutter={16} className="ranking-section">
          <Col span={6}>
            <Spin spinning={isSpinning}>
              <RankingCard title={strings.reportingTopBookersToday} rankData={ratingData?.topBookers ?? null} />
            </Spin>
          </Col>
          <Col span={6}>
            <Spin spinning={isSpinning}>
              <RankingCard title={strings.reportingTopCallersToday} rankData={convertDataToPercentageRating(ratingData?.nearestAppointmentsPerToday ?? null)} />
            </Spin>
          </Col>
          <Col span={6}>
            <Spin spinning={isSpinning}>
              <RankingCard title={strings.reportingHighestBookingRateToday} rankData={convertDataToPercentageRating(ratingData?.mostBookingsPerAssignment ?? null)} />
            </Spin>
          </Col>
          <Tooltip title={strings.reportingShowRateClarification}>
            <Col span={6}>
              <Spin spinning={isSpinning}>
                <RankingCard title={strings.reportingShowRateLeaderboard} rankData={convertDataToPercentageRating(ratingData?.topShowRate ?? null)} />
              </Spin>
            </Col>
          </Tooltip>
        </Row>
      </div>
    </>
  );
};

export default ReportingPage;
